// ===== BEGIN FILE: src/components/ShodashaVargaTable.tsx =====
import * as React from 'react';
import { useMemo } from 'react';
import { Box, Typography } from '@mui/joy';
import {
  computeImplementedVargas,
  vargaToPlacements,
  RASHI_NAMES,
  classifyFunctionalForLagna
} from '../utils/astroEngine';
import { getSignLord } from '../utils/lordshipUtils';

// NOTE: we no longer use computeHouseScoresWithAspects; advanced logic is inline here.
import { computeHouseScoresWithLordsAndAspects } from '../utils/houseScores';
import type { AspectEdge } from '../utils/houseScores';

// -----------------------------
// Types & constants
// -----------------------------

// Planets (no Lagna here)
type PlanetName =
  | 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

// Columns for the table (includes Lagna as a display column)
const COLS = ['Lagna','Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'] as const;
type ColKey = typeof COLS[number];

// Order of vargas (rows)
const VARGAS = [
  'D1','D2','D3','D4','D7','D9','D10','D12','D16','D20','D24','D27','D30','D40','D45','D60'
] as const;
type VargaKey = typeof VARGAS[number];

// Keep a single canonical list for scoring/percent math (excludes Lagna)
const PLANET_COLS: PlanetName[] = [
  'Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'
];

// Signed % per planet ([-100..100]) used for aspect/house scoring
export type PlanetSignedPct = Partial<Record<PlanetName, number>>;

// -----------------------------
// Helpers
// -----------------------------
export type ShodashavargaProps = {
  ascLonDeg: number; // D1 ascendant sidereal longitude (deg)
  planetLongitudesDeg: Partial<Record<PlanetName, number>>;
  showTotals?: boolean;

  /** Optional: planet-wise Shadbala percentages (0..100) */
  shadbalaPct?: Partial<Record<PlanetName, number>>;

  /** Optional: Iá¹£á¹­a/Kaá¹£á¹­a Phala (0..60) per planet */
  ishtaKashta?: Partial<Record<PlanetName, { ishta: number; kashta: number }>>;

  /** D1 aspects: use your Calculator tab output (from, toHouse 1..12) */
  aspectsD1?: AspectEdge[];

  /** Optional override: per-planet signed % in [-100..100].
   *  If not given, we'll derive from Blended% (or from SV% deviation). */
  planetSignedPct?: PlanetSignedPct;
};

/** Small helper: sign index (1..12) from longitude */
function signNumFromLon(lonDeg: number) {
  const idx0 = Math.floor((((lonDeg % 360) + 360) % 360) / 30);
  return idx0 + 1;
}

/** Display helper: neat sign name */
function signNameFromNum(num: number) {
  return RASHI_NAMES[((num - 1) % 12 + 12) % 12];
}
/** Normalize engine outputs: accept 1..12 or 0..11 and return 1..12 */
function normalizeSignNum(x: any): number {
  const n = Number(x);
  if (!Number.isFinite(n)) return NaN;
  if (n >= 1 && n <= 12) return n;     // already 1-based
  if (n >= 0 && n <= 11) return n + 1; // convert 0-based to 1-based
  return NaN;                          // anything else -> invalid
}

/** BASELINE scoring for a planetâ€™s placement in a varga sign. */
const OWN_SIGN: Record<Exclude<PlanetName, 'Rahu'|'Ketu'>, number[]> = {
  Sun:     [5],
  Moon:    [4],
  Mars:    [1, 8],
  Mercury: [3, 6],
  Jupiter: [9, 12],
  Venus:   [2, 7],
  Saturn:  [10, 11],
};

const MOOLATRIKONA: Partial<Record<Exclude<PlanetName, 'Rahu'|'Ketu'>, number>> = {
  Sun: 5,
  Moon: 4,
  Mars: 1,
  Mercury: 6,
  Jupiter: 9,
  Venus: 7,
  Saturn: 11,
};

const EXALT_SIGN: Record<PlanetName, number> = {
  Sun: 1,  Moon: 2,  Mars: 10, Mercury: 6,  Jupiter: 4,  Venus: 12, Saturn: 7,
  Rahu: 2, // (Taurus convention)
  Ketu: 8, // (Scorpio convention)
};

const DEBIL_SIGN: Record<PlanetName, number> = {
  Sun: 7,  Moon: 8,  Mars: 4,  Mercury: 12, Jupiter: 10, Venus: 6,  Saturn: 1,
  Rahu: 8, // (Scorpio)
  Ketu: 2, // (Taurus)
};

// Permanent friendship (classical)
const PERM_FRIENDS: Record<Exclude<PlanetName, 'Rahu'|'Ketu'>, PlanetName[]> = {
  Sun:     ['Moon','Mars','Jupiter'],
  Moon:    ['Sun','Mercury'],
  Mars:    ['Sun','Moon','Jupiter'],
  Mercury: ['Sun','Venus'],
  Jupiter: ['Sun','Moon','Mars'],
  Venus:   ['Mercury','Saturn'],
  Saturn:  ['Mercury','Venus'],
};

const PERM_ENEMIES: Record<Exclude<PlanetName, 'Rahu'|'Ketu'>, PlanetName[]> = {
  Sun:     ['Venus','Saturn'],
  Moon:    [],
  Mars:    ['Mercury'],
  Mercury: ['Moon'],
  Jupiter: ['Venus'],
  Venus:   ['Sun','Moon'],
  Saturn:  ['Sun','Moon','Mars'],
};

// --- Five-fold friendship helpers ---
const TEMP_FRIENDLY_OFFSETS = new Set([2,3,4,10,11,12]);
function offset(fromHouse: number, toHouse: number): number {
  return ((toHouse - fromHouse + 12) % 12) + 1;
}
// Use SAME-VARGA houses (not D1) for temporary friendship
function temporaryRelationByHouses(
  planetHouse: number | undefined,
  lordHouse: number | undefined
): 'friend'|'enemy'|'neutral' {
  if (!planetHouse || !lordHouse) return 'neutral';
  const o1 = offset(planetHouse, lordHouse);
  const o2 = offset(lordHouse, planetHouse);
  const friendly = TEMP_FRIENDLY_OFFSETS.has(o1) && TEMP_FRIENDLY_OFFSETS.has(o2);
  return friendly ? 'friend' : 'enemy';
}
function permanentRelation(planet: PlanetName, lord: PlanetName): 'friend'|'enemy'|'neutral' {
  if (planet === 'Rahu' || planet === 'Ketu') return 'neutral';
  if (PERM_FRIENDS[planet as Exclude<PlanetName,'Rahu'|'Ketu'>]?.includes(lord)) return 'friend';
  if (PERM_ENEMIES[planet as Exclude<PlanetName,'Rahu'|'Ketu'>]?.includes(lord)) return 'enemy';
  return 'neutral';
}
function compoundToScore(
  perm: 'friend'|'enemy'|'neutral',
  temp: 'friend'|'enemy'|'neutral'
): { bandhu: 'great_friend'|'friend'|'neutral'|'enemy'|'great_enemy', bonus: number } {
  const v = (perm === 'friend' ? 1 : perm === 'enemy' ? -1 : 0) + (temp === 'friend' ? 1 : temp === 'enemy' ? -1 : 0);
  if (v >= 2)  return { bandhu: 'great_friend', bonus: +0.75 };
  if (v === 1) return { bandhu: 'friend',       bonus: +0.25 };
  if (v === 0) return { bandhu: 'neutral',      bonus:  0.00 };
  if (v === -1) return { bandhu: 'enemy',       bonus: -0.25 };
  return { bandhu: 'great_enemy', bonus: -0.75 }; // v <= -2
}
/**
 * Baseline scoring now considers exalt/debil, mÅ«latrikoá¹‡a, own sign,
 * and five-fold friendship to the SIGN LORD of the varga sign.
 */
function baselineScore(
  planet: PlanetName,
  signNum: number,
  planetHouse?: number,     // the planetâ€™s house in THIS varga (1..12)
  signLordHouse?: number    // the sign lordâ€™s house in THIS varga (1..12)
): number {
  if (EXALT_SIGN[planet] === signNum) return 2.0;
  if (DEBIL_SIGN[planet] === signNum) return -2.0;
  if (planet === 'Rahu' || planet === 'Ketu') return 0;
  if (MOOLATRIKONA[planet as Exclude<PlanetName,'Rahu'|'Ketu'>] === signNum) return 1.5;
  if (OWN_SIGN[planet as Exclude<PlanetName,'Rahu'|'Ketu'>]?.includes(signNum)) return 1.0;

  const lord = getSignLord(signNum);
  const perm = permanentRelation(planet, lord);
  const temp = temporaryRelationByHouses(planetHouse, signLordHouse);
  const { bonus } = compoundToScore(perm, temp);
  return bonus; // ±0.75 / ±0.25 / 0
}

const cellStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid #eee',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: 'rgba(0, 0, 0, 0.75)'
};

const headCellStyle: React.CSSProperties = {
  ...cellStyle,
  fontWeight: 600,
  borderBottom: '1px solid #ddd',
  background: 'var(--joy-palette-neutral-softBg, #f7f7f7)',
  color: 'rgba(0, 0, 0, 0.85)'
};
const stickyHeadCellStyle: React.CSSProperties = {
  ...headCellStyle,
  position: 'sticky' as any,
  top: 0,
  zIndex: 3,
  background: 'var(--joy-palette-neutral-softBg, #f7f7f7)'
};

/* ======================
   Advanced house scoring
   ====================== */

type FuncRole = 'benefic'|'malefic'|'neutral';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function computeHouseScoresAdvanced(
  planetBlendedPct: Required<Record<PlanetName, number>>,      // per-planet signed %, [-100..100]
  placementsD1: Record<PlanetName, { house: number }>,         // D1 houses (1..12)
  aspects: Array<{ from: PlanetName; toHouse: number }>,       // ParÄÅ›ari aspects (toHouse 1..12)
  ascSign1to12: number
): number[] {
  // Functional map for this Lagna (Sun..Saturn), nodes default to neutral
  const funcMap = classifyFunctionalForLagna(ascSign1to12) as Record<string, FuncRole>;
  const funcRole = (p: PlanetName): FuncRole => (funcMap[p] ?? 'neutral');

  // Houseâ†'occupants, Houseâ†'aspectors
  const occupants: Record<number, PlanetName[]> = Object.fromEntries([...Array(12)].map((_,i)=>[i+1,[]])) as any;
  for (const p of Object.keys(placementsD1) as PlanetName[]) {
    const h = placementsD1[p]?.house|0;
    if (h>=1 && h<=12) occupants[h].push(p);
  }
  const aspectors: Record<number, PlanetName[]> = Object.fromEntries([...Array(12)].map((_,i)=>[i+1,[]])) as any;
  for (const e of aspects) { if (e.toHouse>=1 && e.toHouse<=12) aspectors[e.toHouse].push(e.from); }

  // Houseâ†'sign & lord
  const houseSign = (h:number) => ((ascSign1to12 - 1 + (h - 1)) % 12) + 1;
  const houseLord: Record<number, PlanetName> = {} as any;
  for (let h=1; h<=12; h++) houseLord[h] = getSignLord(houseSign(h));

  // Weights
  const occupyW = { benefic:+1.0, malefic:-1.0, neutral:+1.0 };
  const aspectW = { empty: 1.0, occupied: 0.5 };
  const lordW   = { emptyNoAspect:1.0, emptyHasAspect:0.75, occupied:0.5 };

  // Scores
  const score = Array(12).fill(0) as number[];

  for (let h=1; h<=12; h++) {
    const occ = occupants[h];
    const hasOcc = occ.length > 0;
    const asps = aspectors[h];
    const hasAsp = asps.length > 0;

    // a) House-lord bonus
    const lord = houseLord[h];
    const lordPct = planetBlendedPct[lord] ?? 0;
    if (!hasOcc && !hasAsp) score[h-1] += lordW.emptyNoAspect * lordPct;
    else if (!hasOcc && hasAsp) score[h-1] += lordW.emptyHasAspect * lordPct;
    else score[h-1] += lordW.occupied * lordPct;

    // b) Occupants (signed by functional role)
    for (const p of occ) {
      const role = funcRole(p);
      const w = occupyW[role];
      score[h-1] += w * (planetBlendedPct[p] ?? 0);
    }

    // c) Aspects (signed; weight by empty/occupied)
    const wAsp = hasOcc ? aspectW.occupied : aspectW.empty;
    for (const p of asps) {
      const role = funcRole(p);
      const signed = (role === 'malefic') ? -1 : +1; // neutral treated as + by default
      score[h-1] += wAsp * signed * (planetBlendedPct[p] ?? 0);
    }

    // Optional clamp for UI sanity
    const clampTo = 400;
    score[h-1] = Math.max(-clampTo, Math.min(clampTo, score[h-1]));
  }

  return score;
}

// -----------------------------
// Component
// -----------------------------
export default function ShodashaVargaTable({
  ascLonDeg,
  planetLongitudesDeg,
  showTotals,
  shadbalaPct,
  ishtaKashta,
  aspectsD1,
  planetSignedPct
}: ShodashavargaProps) {

  const data = useMemo(() => {
    // Build varga map once from the SAME inputs the calculator used
    const longs: Record<PlanetName, number> = {
      Sun: planetLongitudesDeg.Sun ?? NaN,
      Moon: planetLongitudesDeg.Moon ?? NaN,
      Mars: planetLongitudesDeg.Mars ?? NaN,
      Mercury: planetLongitudesDeg.Mercury ?? NaN,
      Jupiter: planetLongitudesDeg.Jupiter ?? NaN,
      Venus: planetLongitudesDeg.Venus ?? NaN,
      Saturn: planetLongitudesDeg.Saturn ?? NaN,
      Rahu: planetLongitudesDeg.Rahu ?? NaN,
      Ketu: planetLongitudesDeg.Ketu ?? NaN,
    };

    const vargaMap = computeImplementedVargas(ascLonDeg, longs) as Record<string, any>;

    // Helper: pull a planet's direct varga sign from common shapes, if present
    const getDirectVargaSign = (vargaObj: any, planet: PlanetName): number | undefined => {
      if (vargaObj?.planetSigns && typeof vargaObj.planetSigns[planet] !== 'undefined') {
        const raw = vargaObj.planetSigns[planet];
        const norm = normalizeSignNum(raw);
        return Number.isFinite(norm) ? norm : undefined;
      }
      const key = `${planet}Sign`;
      if (typeof vargaObj?.[key] !== 'undefined') {
        const norm = normalizeSignNum(vargaObj[key]);
        return Number.isFinite(norm) ? norm : undefined;
      }
      if (vargaObj?.planets && typeof vargaObj.planets[planet]?.sign !== 'undefined') {
        const norm = normalizeSignNum(vargaObj.planets[planet].sign);
        return Number.isFinite(norm) ? norm : undefined;
      }
      return undefined;
    };

    // Build table rows
    const rows: Array<{
      key: VargaKey;
      lagnaSign: number;
      signs: Partial<Record<PlanetName, number>>;
      scores: Partial<Record<PlanetName, number>>;
      rowTotal: number;
    }> = [];

    for (const vk of VARGAS) {
      const v = vargaMap[vk];
      if (!v) continue;

      const lagnaSign: number = v.ascSign; // 1..12
      const placements = vargaToPlacements(v); // fallback structure

      const signs: Partial<Record<PlanetName, number>> = {};
      const scores: Partial<Record<PlanetName, number>> = {};
      let total = 0;

      (PLANET_COLS as PlanetName[]).forEach((p) => {
        // Classical baseline: DO NOT subdivide nodes; show D1 only
        if ((p === 'Rahu' || p === 'Ketu') && vk !== 'D1') {
          signs[p] = NaN;         // display as —
          scores[p] = NaN;        // hide per-varga score for nodes beyond D1
          return;
        }

        // 1) Prefer engine-provided varga sign if exposed
        let sign = getDirectVargaSign(v, p);

        // 2) Fallback: infer from varga house using varga lagna
        if (!Number.isFinite(sign as number)) {
          const ph = placements[p]?.house;
          if (ph) sign = ((lagnaSign - 1 + ph - 1) % 12) + 1; // 1..12
        }

        // 3) Last-ditch (only for D1): compute from raw longitude
        if (!Number.isFinite(sign as number) && vk === 'D1') {
          const lon = longs[p];
          if (typeof lon === 'number' && Number.isFinite(lon)) {
            sign = signNumFromLon(lon);
          }
        }

        // Scoring call (five-fold with houses)
        const sn = Number.isFinite(sign as number) ? normalizeSignNum(sign) : NaN;
        signs[p] = sn;

        let s = 0;
        if (Number.isFinite(sn)) {
          const planetHouse = placements[p]?.house;  // 1..12 in THIS varga
          const lord = getSignLord(sn);              // lord of the sign planet occupies
          const lordHouse  = placements[lord as Exclude<PlanetName,'Rahu'|'Ketu'>]?.house; // lord's house in THIS varga
          s = baselineScore(p, sn, planetHouse, lordHouse);
        }
        scores[p] = s;
        total += s;
      });

      rows.push({ key: vk, lagnaSign, signs, scores, rowTotal: total });
    }

    // Totals per column if requested
    const colTotals: Partial<Record<ColKey, number>> = {};
    if (showTotals) {
      for (const col of COLS) {
        let sum = 0;
        if (col === 'Lagna') {
          sum = 0; // lagna baseline = 0
        } else {
          for (const r of rows) sum += r.scores[col as PlanetName] ?? 0;
        }
        colTotals[col] = sum;
      }
    }

    return { rows, colTotals, vargaMap };
  }, [ascLonDeg, planetLongitudesDeg, showTotals]);

  // --- Percentages setup (outside useMemo) ---
  const MAX_PER_CELL = 2;

  // Row-wise %
  const rowTotalsPct: Record<VargaKey, number> = {} as any;
  data.rows.forEach(r => {
    const validCount = PLANET_COLS.reduce((acc, p) => {
      const node = (p === 'Rahu' || p === 'Ketu');
      const valid = Number.isFinite(r.signs[p] as number) && (!node || r.key === 'D1');
      return acc + (valid ? 1 : 0);
    }, 0);
    const maxRow = validCount * MAX_PER_CELL;
    rowTotalsPct[r.key] = maxRow > 0 ? Math.round((r.rowTotal / maxRow) * 100) : 0;
  });

  // Column-wise % (+ grand total)
  const colTotalsPct: Partial<Record<ColKey, number>> = {};
  let grandSum = 0;
  let grandMax = 0;

  COLS.forEach(c => {
    if (c === 'Lagna') {
      colTotalsPct[c] = undefined;
      return;
    }
    const p = c as PlanetName;
    const validRows = data.rows.reduce((acc, r) => {
      const node = (p === 'Rahu' || p === 'Ketu');
      const valid = Number.isFinite(r.signs[p] as number) && (!node || r.key === 'D1');
      return acc + (valid ? 1 : 0);
    }, 0);
    const maxCol = validRows * MAX_PER_CELL;
    const sum = data.colTotals[c] ?? 0;

    colTotalsPct[c] = maxCol > 0 ? Math.round((sum / maxCol) * 100) : 0;

    grandSum += sum;
    grandMax += maxCol;
  });

  const grandPct = grandMax > 0 ? Math.round((grandSum / grandMax) * 100) : 0;

  // --- Calibrated Shodashavarga% (neutral = 50) ---
  const svPctCalibrated: Partial<Record<ColKey, number>> = {};
  COLS.forEach(c => {
    if (c === 'Lagna') { svPctCalibrated[c] = undefined; return; }
    const p = c as PlanetName;
    let sum = 0, n = 0;
    for (const r of data.rows) {
      // Nodes only in D1 per your rule
      const isNode = (p === 'Rahu' || p === 'Ketu');
      if (isNode && r.key !== 'D1') continue;
      const s = r.scores[p];
      if (typeof s === 'number' && isFinite(s)) {
        const cell01 = Math.max(0, Math.min(1, (s + 2) / 4)); // [-2..+2] -> [0..1]
        sum += cell01;
        n += 1;
      }
    }
    svPctCalibrated[c] = n ? Math.round((sum / n) * 100) : undefined;
  });

  // --- Iá¹£á¹­a/Kaá¹£á¹­a + Blended % helpers (soft-saturating) ---
  const K = 3.0;
  const blendedPct: Partial<Record<ColKey, number>> = {};
  COLS.forEach(c => {
    if (c === 'Lagna') { blendedPct[c] = undefined; return; }
    const p = c as PlanetName;

    const sv = svPctCalibrated[c];   // calibrated SV%
    const sb = shadbalaPct?.[p];     // Shadbala %
    const ik = ishtaKashta?.[p];     // { ishta, kashta }

    if (!Number.isFinite(sv) || !Number.isFinite(sb) || !ik || !Number.isFinite(ik.ishta) || !Number.isFinite(ik.kashta)) {
      blendedPct[c] = undefined;
      return;
    }

    const sv01 = (sv as number) / 100;           // 0..1
    const sb01 = (sb as number) / 100;           // 0..1
    const svEff = Math.min(0.97, Math.max(0, sv01));
    const sbEff = Math.min(0.97, Math.max(0, sb01));
    const dirRaw = (ik.ishta - ik.kashta) / 60;  // -1..+1
    const dirEff = Math.max(-0.85, Math.min(0.85, dirRaw));

    const base = svEff * sbEff * dirEff;         // signed
    const blendedSigned = 100 * Math.tanh(K * base);

    blendedPct[c] = Math.round(blendedSigned * 10) / 10; // one decimal
  });

  // -----------------------------
  // Aspect-aware D1 House Scores â€” ADVANCED per your spec
  // -----------------------------
  // Effective per-planet signed% in [-100..100]
  const effectiveSignedPct: Record<PlanetName, number> = (() => {
    // Priority: explicit prop -> blended% -> derived from SV deviation
    const out: Record<PlanetName, number> = {
      Sun: 0, Moon: 0, Mars: 0, Mercury: 0, Jupiter: 0, Venus: 0, Saturn: 0, Rahu: 0, Ketu: 0
    };
    for (const p of PLANET_COLS) {
      const fromProp = planetSignedPct?.[p];
      const fromBlend = blendedPct[p as ColKey];
      const fromSV = svPctCalibrated[p as ColKey];

      if (typeof fromProp === 'number' && isFinite(fromProp)) {
        out[p] = Math.max(-100, Math.min(100, fromProp));
      } else if (typeof fromBlend === 'number' && isFinite(fromBlend)) {
        out[p] = Math.max(-100, Math.min(100, fromBlend));
      } else if (typeof fromSV === 'number' && isFinite(fromSV)) {
        // map SV% (0..100, 50 neutral) -> signed [-100..+100]
        out[p] = Math.max(-100, Math.min(100, (fromSV - 50) * 2));
      }
    }
    return out;
  })();

  // Build D1 placements (planet -> house)
  const placementsD1: Record<PlanetName, { house: number }> = (() => {
    const vD1 = (data as any).vargaMap?.D1;
    const pl = vargaToPlacements(vD1) || {};
    const out: Record<PlanetName, { house: number }> = {
      Sun: { house: 0 as any }, Moon: { house: 0 as any }, Mars: { house: 0 as any },
      Mercury: { house: 0 as any }, Jupiter: { house: 0 as any }, Venus: { house: 0 as any },
      Saturn: { house: 0 as any }, Rahu: { house: 0 as any }, Ketu: { house: 0 as any }
    };
    for (const p of PLANET_COLS) {
      const h = pl[p]?.house;
      if (typeof h === 'number' && h >= 1 && h <= 12) out[p] = { house: h };
    }
    return out;
  })();

  const ascSignD1 = signNumFromLon(ascLonDeg);

// …replace the housePctD1 useMemo:
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const housePctD1: number[] = useMemo(() => {
  const aspects = aspectsD1 ?? [];
  return computeHouseScoresWithLordsAndAspects(
    effectiveSignedPct,
    placementsD1,
    aspects,
    ascSignD1,
    { clampTo: 100 }
  ).map(v => Math.round(v));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [JSON.stringify(placementsD1), JSON.stringify(effectiveSignedPct), JSON.stringify(aspectsD1), ascSignD1]);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Typography level="title-md" sx={{ mb: 1 }}>
  Śoḍaśavarga — Working Scoring Baseline
      </Typography>

      {/* Scroll container: vertical scroll; header sticks on top */}
      <div style={{ maxHeight: '60vh', overflowY: 'auto', position: 'relative' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...stickyHeadCellStyle, textAlign: 'left' }}>Varga</th>
              {COLS.map(col => (
                <th key={col} style={stickyHeadCellStyle}>{col}</th>
              ))}
              <th style={stickyHeadCellStyle}>Row Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.key}>
                <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600 }}>{r.key}</td>

                {/* Lagna cell */}
                <td style={cellStyle} title={`Lagna in ${signNameFromNum(r.lagnaSign)}`}>
                  {signNameFromNum(r.lagnaSign)}
                  <div style={{ fontSize: 11, opacity: 0.7 }}>score: 0</div>
                </td>

                {/* Planet cells */}
                {(PLANET_COLS as PlanetName[]).concat([]).map(p => {
                  const sign = r.signs[p];
                  const scr  = r.scores[p];
                  const label = Number.isFinite(sign as number) && (sign as number) >= 1
                    ? signNameFromNum(sign as number)
                    : '—';

                  return (
                    <td key={p} style={cellStyle} title={`${p} in ${label}`}>
                      {label}
                      <div style={{ fontSize: 11, opacity: 0.7 }}>
                        score: {Number.isFinite(scr as number) ? Number(scr).toFixed(2) : '—'}
                      </div>
                    </td>
                  );
                })}

                <td style={cellStyle}>
                  <strong>{r.rowTotal.toFixed(2)}</strong>
                  <div style={{ fontSize: 11, opacity: 0.7 }}>
                    {rowTotalsPct[r.key]}%
                  </div>
                </td>
              </tr>
            ))}
          </tbody>

          {(showTotals || shadbalaPct || ishtaKashta) && (
            <tfoot>
              {/* 1) Totals row (Shodashavarga sums + %, signed average around 0) */}
              {showTotals && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}>
                    <strong>Totals</strong>
                    <div style={{ fontSize: 10, opacity: 0.6 }}>(Signed SV%, neutral = 0)</div>
                  </td>
                  {COLS.map(c => {
                    const sum = data.colTotals[c] ?? 0;
                    const pct = colTotalsPct[c];
                    return (
                      <td key={c} style={headCellStyle}>
                        <div><strong>{sum.toFixed(2)}</strong></div>
                        {c !== 'Lagna' ? (
                          <div style={{ fontSize: 11, opacity: 0.75 }}>{pct}%</div>
                        ) : (
                          <div style={{ fontSize: 11, opacity: 0.5 }}>—</div>
                        )}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>
                    <div><strong>{grandSum.toFixed(2)}</strong></div>
                    <div style={{ fontSize: 11, opacity: 0.75 }}>{grandPct}%</div>
                  </td>
                </tr>
              )}

              {/* 1b) Calibrated Shodashavarga % (0-100 scale, neutral = 50) */}
              {showTotals && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}>
                    <strong>Calibrated Shodashavarga %</strong>
                    <div style={{ fontSize: 10, opacity: 0.6 }}>(0–100 scale, neutral = 50)</div>
                  </td>
                  {COLS.map(c => {
                    if (c === 'Lagna') return <td key={c} style={headCellStyle}>—</td>;
                    const v = svPctCalibrated[c];
                    return (
                      <td key={c} style={headCellStyle}>
                        {typeof v === 'number' && isFinite(v) ? `${v}%` : '—'}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>—</td>
                </tr>
              )}

              {/* 2) Shadbala % row */}
              {shadbalaPct && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}><strong>Shadbala %</strong></td>
                  {COLS.map(c => {
                    if (c === 'Lagna') return <td key={c} style={headCellStyle}>—</td>;
                    const p = c as PlanetName;
                    const v = shadbalaPct[p];
                    return (
                      <td key={c} style={headCellStyle}>
                        {typeof v === 'number' && isFinite(v) ? `${Math.round(v)}%` : '—'}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>—</td>
                </tr>
              )}

              {/* 3) Iṣṭa (0–60) row */}
              {ishtaKashta && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}><strong>Iṣṭa (0–60)</strong></td>
                  {COLS.map(c => {
                    if (c === 'Lagna') return <td key={c} style={headCellStyle}>—</td>;
                    const p = c as PlanetName;
                    const v = ishtaKashta[p]?.ishta;
                    return (
                      <td key={c} style={headCellStyle}>
                        {typeof v === 'number' && isFinite(v) ? v.toFixed(1) : '—'}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>—</td>
                </tr>
              )}

              {/* 4) Kaṣṭa (0–60) row */}
              {ishtaKashta && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}><strong>Kaṣṭa (0–60)</strong></td>
                  {COLS.map(c => {
                    if (c === 'Lagna') return <td key={c} style={headCellStyle}>—</td>;
                    const p = c as PlanetName;
                    const v = ishtaKashta[p]?.kashta;
                    return (
                      <td key={c} style={headCellStyle}>
                        {typeof v === 'number' && isFinite(v) ? v.toFixed(1) : '—'}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>—</td>
                </tr>
              )}

              {/* 5) Iṣṭa − Kaṣṭa row */}
              {ishtaKashta && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}><strong>Iṣṭa − Kaṣṭa</strong></td>
                  {COLS.map(c => {
                    if (c === 'Lagna') return <td key={c} style={headCellStyle}>—</td>;
                    const p = c as PlanetName;
                    const ishta = ishtaKashta[p]?.ishta;
                    const kashta = ishtaKashta[p]?.kashta;
                    const diff = (typeof ishta === 'number' && typeof kashta === 'number')
                      ? (ishta - kashta)
                      : NaN;
                    return (
                      <td key={c} style={headCellStyle}>
                        {Number.isFinite(diff) ? diff.toFixed(1) : '—'}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>—</td>
                </tr>
              )}

              {/* 6) Blended % row */}
              {(showTotals || shadbalaPct || ishtaKashta) && (
                <tr>
                  <td style={{ ...headCellStyle, textAlign: 'left' }}><strong>Blended %</strong></td>
                  {COLS.map(c => {
                    if (c === 'Lagna') return <td key={c} style={headCellStyle}>—</td>;
                    const v = blendedPct[c];
                    return (
                      <td key={c} style={headCellStyle}>
                        {typeof v === 'number' && isFinite(v) ? `${v}%` : '—'}
                      </td>
                    );
                  })}
                  <td style={headCellStyle}>—</td>
                </tr>
              )}
            </tfoot>
          )}
        </table>
      </div> {/* <-- CLOSE the scroll wrapper */}
    </Box>
  );
}
// ===== END FILE: src/components/ShodashaVargaTable.tsx =====
